import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
} from "@mui/material";
import { Print } from "@mui/icons-material";
import { useRef, useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";
import CustomFormDialog from "../../Components/CustomFormDialog";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";

const CustomerDetails = () => {
    const { id } = useParams();
    const authHeader = useAuthHeader();

    const [customer, setCustomer] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [totalRemaining, setTotalRemaining] = useState(0);
    const [payments, setPayments] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(true);
    const recordsRef = useRef(null);
    const reactToPrintFn = useReactToPrint({
        contentRef: recordsRef,
        documentTitle: `${customer?.name || "customer"}-records`,
        pageStyle: `@page { size: auto;  margin: 15mm; }`,
    });

    const [isPayDialogOpen, setIsPayDialogOpen] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);

    // Fetch customer details
    const fetchCustomerDetails = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/otherparty/${id}`,
                {
                    headers: { Authorization: authHeader },
                }
            );
            if (response.ok) {
                const { otherparty } = await response.json();
                setCustomer(otherparty);
            }
        } catch (error) {
            toast.error("Error fetching customer details.");
        }
    };

    // Fetch invoices for this customer
    const fetchInvoices = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/invoices?otherPartyId=${id}`,
                { headers: { Authorization: authHeader } }
            );
            if (response.ok) {
                const { data } = await response.json();
                setInvoices(data);

                // Compute unpaid total remaining amount
                const unpaid = data.filter(
                    (inv) => Number(inv.remainingAmount) > 0
                );
                const total = unpaid.reduce(
                    (sum, invoice) => sum + Number(invoice.remainingAmount),
                    0
                );
                setTotalRemaining(total);
                setLoadingRecords(false);
            }
        } catch (error) {
            toast.error("Error fetching unpaid invoices.");
            setLoadingRecords(false);
        }
    };

    // Fetch previous payments
    const fetchPayments = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/payments/byOtherParty/${id}`,
                { headers: { Authorization: authHeader } }
            );
            if (response.ok) {
                const { payments } = await response.json();
                setPayments(payments);
                setLoadingRecords(false);
            }
        } catch (error) {
            toast.error("Error fetching payments.");
            setLoadingRecords(false);
        }
    };

    const handleSubmitPayment = async () => {
        console.log(paymentAmount);
        if (!paymentAmount || isNaN(paymentAmount) || paymentAmount <= 0) {
            toast.error("Please enter a valid payment amount.");
            return;
        }

        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/payments/pay`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify({
                        otherPartyId: id,
                        amount: parseFloat(paymentAmount),
                        currency: "USD",
                        rateAtPayment: 1.0,
                    }),
                }
            );
            if (response.ok) {
                toast.success("Payment applied successfully!");
                setIsPayDialogOpen(false);
                fetchInvoices();
                fetchPayments();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || "Error processing payment.");
            }
        } catch (error) {
            toast.error("Error submitting payment.");
        }
    };

    useEffect(() => {
        const fetchAll = async () => {
            setLoadingRecords(true);
            await Promise.all([
                fetchCustomerDetails(),
                fetchInvoices(),
                fetchPayments(),
            ]);
            setLoadingRecords(false);
        };
        fetchAll();
    }, []);

    // memoize combined records so we don't recompute every render
    const combinedRecords = (() => {
        const invoiceMap = {};
        invoices.forEach((inv) => {
            invoiceMap[inv.id] = inv;
        });
        const invoiceRows = invoices.map((inv) => ({
            id: `inv-${inv.id}`,
            date: new Date(inv.createdAt),
            type: "Invoice",
            reference: inv.serialId || inv.id,
            amount: Number(inv.priceFinal) || 0,
            remaining: Number(inv.remainingAmount) || 0,
        }));
        const paymentRows = payments.map((p) => ({
            id: `pay-${p.id}`,
            date: new Date(p.createdAt),
            type: "Payment",
            reference: invoiceMap[p.invoiceId]?.serialId || p.invoiceId,
            amount: Number(p.amount) || 0,
            remaining: "-",
        }));
        const combined = [...invoiceRows, ...paymentRows];
        const combinedAsc = combined.slice().sort((a, b) => a.date - b.date);
        let cumulative = 0;
        combinedAsc.forEach((row) => {
            if (row.type === "Invoice") cumulative += row.amount;
            else cumulative -= row.amount;
            row.cumulativeBalance = cumulative;
        });
        return combinedAsc.slice().reverse(); // desc
    })();

    // Current overall balance (most recent cumulative balance)
    const currentBalance =
        (combinedRecords.length && combinedRecords[0].cumulativeBalance) || 0;

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 4,
        }).format(value);

    return (
        <Box>
            <Typography variant="h4">{customer.name}'s Details</Typography>
            <Divider sx={{ marginY: 2 }} />

            {/* Records (Invoices & Payments) */}
            <Typography variant="h6">Invoices & Payments</Typography>
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => reactToPrintFn?.()}
                    disabled={loadingRecords}
                    startIcon={<Print />}
                >
                    Print Records
                </Button>
            </Box>
            <div ref={recordsRef}>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Dannawi Med Lab
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                        {customer?.name || ""} - {customer?.phone || ""}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Balance</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Build combined list and sort by date desc */}
                        {(() => {
                            // memoized combinedRecords computed above
                            return combinedRecords.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        {row.date.toLocaleString()}
                                    </TableCell>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>{row.reference}</TableCell>
                                    <TableCell>
                                        {formatCurrency(row.amount)}
                                    </TableCell>
                                    <TableCell>
                                        {formatCurrency(
                                            row.cumulativeBalance || 0
                                        )}
                                    </TableCell>
                                </TableRow>
                            ));
                        })()}
                    </TableBody>
                </Table>
            </div>
            <Typography variant="h6">
                Total Remaining Amount: {formatCurrency(currentBalance)}
            </Typography>

            <Button
                variant="contained"
                color="primary"
                onClick={() => setIsPayDialogOpen(true)}
                sx={{ marginTop: 2 }}
            >
                Pay Now
            </Button>

            {/* Payment Dialog */}
            {/* <CustomFormDialog
                title="Make a Payment"
                fields={[
                    {
                        label: "Payment Amount",
                        name: "amount",
                        type: "number",
                        placeholder: "Enter Ammount",
                        required: true,
                        width: "98%",
                        onFieldChange: (allFields, value) => {
                            console.log(value);
                            setPaymentAmount(value);
                            console.log({ allFields });
                            return allFields;
                        },
                    },
                ]}
                open={isPayDialogOpen}
                onClose={() => setIsPayDialogOpen(false)}
                handleSubmitClick={handleSubmitPayment}
                formSubmitText="Submit Payment"
            /> */}

            <Dialog
                open={isPayDialogOpen}
                onClose={() => setIsPayDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <Typography variant="h6">Make a Payment</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Enter the amount you wish to pay:
                    </Typography>
                    <TextField
                        label="Payment Amount"
                        type="number"
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        value={paymentAmount}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        onClick={() => setIsPayDialogOpen(false)}
                        color="primary"
                        variant="outlined "
                        style={{ marginRight: 10 }}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmitPayment}
                        color="primary"
                        variant="contained"
                    >
                        Submit Payment
                    </Button>
                </DialogActions>
            </Dialog>
            <Divider sx={{ marginY: 2 }} />

            {/* Payment History */}
            <Typography variant="h6">(Legacy) Payment History</Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Payment ID</TableCell>
                        <TableCell>Invoice ID</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {payments.map((payment) => (
                        <TableRow key={payment.id}>
                            <TableCell>{payment.id}</TableCell>
                            <TableCell>
                                {invoices.find(
                                    (inv) => inv.id === payment.invoiceId
                                )?.serialId || payment.invoiceId}
                            </TableCell>
                            <TableCell>
                                {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell>
                                {new Date(payment.createdAt).toLocaleString()}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default CustomerDetails;
